import React, { useState, useEffect, useRef } from 'react';
import { auditsApi } from '../services/api';
import './AuditTemplate.css';

interface AuditTemplateProps {
  auditId: number;
  onClose: () => void;
  onComplete: (data: any) => void;
  user?: {
    id: number;
    name: string;
    role: string;
  };
  initialData?: any;
  supervisorMode?: boolean;
}

interface EventImage {
  id: number;
  data: string;
  name: string;
  size: number;
  timestamp: string;
}

interface AuditEvent {
  id: number;
  numero: number;
  timestamp: string;
  descripcion: string;
  observaciones: string;
  imagenes: EventImage[];
  tipoEvento: string;
  colorEvento: string;
}

interface TemplateData {
  auditoria: {
    id: number;
    titulo: string;
    descripcion: string;
    cliente: string;
    sucursal: string;
    direccion: string;
    prioridad: string;
    observacionesIniciales: string;
    fechaInicio: string;
    horasEstimadas: number;
  };
  eventos: AuditEvent[];
  observacionesFinales: string;
  archivosAdjuntos: Array<{
    nombreOriginal: string;
    nombreArchivo: string;
    tamaño: number;
    tipoMime: string;
  }>;
  sucursalDetails?: string;
  canalesVision?: string;
}

const AuditTemplate: React.FC<AuditTemplateProps> = ({ auditId, onClose, onComplete, user, initialData, supervisorMode = false }) => {
  const [template, setTemplate] = useState<TemplateData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [observacionesFinales, setObservacionesFinales] = useState('');
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [startTime, setStartTime] = useState(new Date());
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sucursalDetails, setSucursalDetails] = useState('');
  const [canalesVision, setCanalesVision] = useState('');
  const [showPauseDialog, setShowPauseDialog] = useState(false);
  const [pauseReason, setPauseReason] = useState('');
  const [autosaveStatus, setAutosaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  const pasteAreaRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const intervalRef = useRef<number | null>(null);
  const assignmentCheckRef = useRef<number | null>(null);
  const alertedAssignments = useRef<Set<number>>(new Set()); // Para evitar alertas repetidas
  const autosaveTimerRef = useRef<number | null>(null);
  const skipFirstAutosave = useRef(true); // No autoguardar en la carga inicial

  useEffect(() => {
    if (initialData) {
      // console.log('🔍 SUPERVISOR MODE - Datos iniciales:', {
      //   auditId: initialData.auditoria?.id,
      //   titulo: initialData.auditoria?.titulo,
      //   eventosOriginales: initialData.eventos?.length || 0,
      //   supervisorMode
      // });

      // Si tenemos datos iniciales (modo supervisor), usarlos directamente
      setTemplate(initialData);
      setObservacionesFinales(initialData.observacionesFinales || '');
      
      // Debug: Ver datos crudos de eventos
      
      // Convertir eventos del backend a la estructura que espera el frontend
      const eventosConvertidos = (initialData.eventos || []).map((evento: any, index: number) => {
        // console.log('🔍 EVENTO DEBUG:', {
        //   originalId: evento.id,
        //   descripcion: evento.descripcion,
        //   timestamp: evento.timestamp,
        //   imagenes: evento.imagenes?.length || 0,
        //   keys: Object.keys(evento)
        // });
        
        return {
          id: evento.id || Date.now() + index,
          numero: index + 1,
          timestamp: evento.timestamp || new Date().toISOString(),
          descripcion: evento.descripcion || '',
          observaciones: evento.observaciones || '',
          imagenes: evento.imagenes || [],
          tipoEvento: evento.tipoEvento || 'infraccion-menor',
          colorEvento: evento.colorEvento || '#0066cc'
        };
      });
      

      setEvents(eventosConvertidos);
      setSucursalDetails(initialData.sucursalDetails || '');
      setCanalesVision(initialData.canalesVision || '');

      // ⏱️ RECUPERAR TIEMPO PREVIO si existe (para auditorías reanudadas)
      const tiempoPrevioMinutos = initialData.auditoria?.tiempoTrabajadoPrevio || 0;
      if (tiempoPrevioMinutos > 0) {
        setElapsedTime(tiempoPrevioMinutos * 60); // Convertir minutos a segundos
        console.log(`✅ Tiempo previo recuperado: ${tiempoPrevioMinutos} minutos (${tiempoPrevioMinutos * 60} segundos)`);
      }

      setLoading(false);
    } else {
      // Si no, cargar normalmente
      loadTemplate();
    }
    
    // Start timer if not in supervisor mode (both operadores and supervisors working as operators need timer)
    if (!supervisorMode) {
      startTimer();
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (assignmentCheckRef.current) {
        clearInterval(assignmentCheckRef.current);
      }
    };
  }, [auditId, user, initialData, supervisorMode]);

  // 🚨 SISTEMA DE DETECCIÓN DE NUEVAS ASIGNACIONES
  useEffect(() => {
    if (!user?.id || supervisorMode) return; // Solo para operadores trabajando

    const checkForNewAssignments = async () => {
      try {
        const response = await auditsApi.getAll({ all: 'true' });
        if (response.data.success) {
          const audits = response.data.auditorias || response.data.data || [];
          const myAssignedAudits = audits.filter((audit: any) =>
            audit.operadorId === user.id &&
            audit.estado === 'asignada' &&
            audit.id !== auditId // No contar la auditoría actual
          );

          if (myAssignedAudits.length > 0) {
            const latestAssignment = myAssignedAudits.sort((a: any, b: any) =>
              new Date(b.fechaAsignacion || b.createdAt).getTime() -
              new Date(a.fechaAsignacion || a.createdAt).getTime()
            )[0];

            // Verificar si es una asignación reciente (últimas 2 horas)
            const assignmentTime = new Date(latestAssignment.fechaAsignacion || latestAssignment.createdAt).getTime();
            const twoHoursAgo = Date.now() - (2 * 60 * 60 * 1000);

            if (assignmentTime > twoHoursAgo && !alertedAssignments.current.has(latestAssignment.id)) {
              alertedAssignments.current.add(latestAssignment.id); // Marcar como alertado
              await handleNewAssignmentDetected(latestAssignment);
            }
          }
        }
      } catch (error) {
        console.error('Error checking for new assignments:', error);
      }
    };

    // Revisar cada 10 segundos
    assignmentCheckRef.current = setInterval(checkForNewAssignments, 10000);

    return () => {
      if (assignmentCheckRef.current) {
        clearInterval(assignmentCheckRef.current);
      }
    };
  }, [user?.id, auditId, supervisorMode]);

  // No agregar evento automáticamente - solo mostrar cuando el usuario haga clic

  useEffect(() => {
    // Global paste handler for images
    const handleGlobalPaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (items) {
        for (let item of items) {
          if (item.type.indexOf('image') !== -1) {
            // Si hay eventos disponibles, agregar al PRIMER evento (no al último)
            if (events.length > 0) {
              const file = item.getAsFile();
              if (file) {
                // Buscar el último evento con foco o el primero disponible
                const targetEvent = events.find(event => document.activeElement?.closest(`.event-item[data-event-id="${event.id}"]`)) || events[0];
                processImage(file, targetEvent.id);
              }
            }
          }
        }
      }
    };

    document.addEventListener('paste', handleGlobalPaste);

    return () => {
      document.removeEventListener('paste', handleGlobalPaste);
    };
  }, [events]);

  // 💾 AUTOGUARDADO POR INACTIVIDAD
  // Cada vez que cambia el contenido se reinicia el temporizador; si pasan
  // AUTOSAVE_IDLE_MS sin más cambios, se guarda el borrador silenciosamente.
  // Solo aplica al operador trabajando su planilla (no en modo supervisor).
  const AUTOSAVE_IDLE_MS = 90000; // 90 segundos sin actividad
  useEffect(() => {
    if (supervisorMode || loading) return;
    // Evitar guardar en el primer render (justo después de cargar los datos)
    if (skipFirstAutosave.current) {
      skipFirstAutosave.current = false;
      return;
    }
    // Nada que guardar todavía
    if (events.length === 0) return;

    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = window.setTimeout(async () => {
      try {
        setAutosaveStatus('saving');
        await auditsApi.autosave(auditId, {
          eventos: events,
          observacionesFinales,
          sucursalDetails,
          canalesVision
        });
        setAutosaveStatus('saved');
      } catch (err) {
        console.error('Error en autoguardado:', err);
        setAutosaveStatus('error');
      }
    }, AUTOSAVE_IDLE_MS);

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
  }, [events, observacionesFinales, sucursalDetails, canalesVision, supervisorMode, loading, auditId]);

  const loadTemplate = async () => {
    try {
      setLoading(true);
      const response = await auditsApi.getTemplate(auditId);
      if (response.data.success) {
        const data = response.data.data;
        setTemplate(data);
        setObservacionesFinales(data.observacionesFinales || '');
        setSucursalDetails(data.sucursalDetails || '');
        setCanalesVision(data.canalesVision || '');

        // ⏱️ RECUPERAR TIEMPO PREVIO si existe (para auditorías reanudadas)
        const tiempoPrevioMinutos = data.auditoria?.tiempoTrabajadoPrevio || 0;
        if (tiempoPrevioMinutos > 0) {
          setElapsedTime(tiempoPrevioMinutos * 60); // Convertir minutos a segundos
          console.log(`✅ Tiempo previo recuperado: ${tiempoPrevioMinutos} minutos (${tiempoPrevioMinutos * 60} segundos)`);
        }

        // Si la auditoría está pausada, reanudarla automáticamente cuando el operador la abre
        if (data.auditoria?.estado === 'pausada' && !supervisorMode) {
          try {
            await auditsApi.resumeAudit(auditId);
            console.log('✅ Auditoría reanudada automáticamente');

            // Recargar datos de la auditoría para actualizar el estado a 'en_proceso'
            const updatedResponse = await auditsApi.getTemplate(auditId);
            const updatedData = updatedResponse.data.data;
            setTemplate(updatedData);
            console.log('✅ Estado actualizado a:', updatedData.auditoria?.estado);
          } catch (resumeError) {
            console.error('Error reanudando auditoría:', resumeError);
          }
        }

        // Si hay eventos existentes (auditoría devuelta para corrección), cargarlos
        if (data.eventos && data.eventos.length > 0) {
          const eventosConvertidos = data.eventos.map((evento: any, index: number) => ({
            id: evento.id || Date.now() + index,
            numero: index + 1,
            timestamp: evento.timestamp || new Date().toISOString(),
            descripcion: evento.descripcion || '',
            observaciones: evento.observaciones || '',
            imagenes: evento.imagenes || [],
            tipoEvento: evento.tipoEvento || 'infraccion-menor',
            colorEvento: evento.colorEvento || '#0066cc'
          }));
          setEvents(eventosConvertidos);
        } else {
          // Solo iniciar con array vacío si no hay eventos previos
          setEvents([]);
        }
      }
    } catch (err: any) {
      setError('Error cargando plantilla: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // 🚨 MANEJAR NUEVA ASIGNACIÓN DETECTADA
  const handleNewAssignmentDetected = async (newAssignment: any) => {
    try {
      // Solo alertar si hay un trabajo en progreso (timer activo y no pausado)
      // Si es ciclo normal (primer trabajo), no molestar al usuario
      if (intervalRef.current && !isPaused) {
        // HAY INTERRUPCIÓN REAL - pausar y alertar
        pauseTimer();
        setIsPaused(true);

        // Cambiar título del navegador para llamar la atención
        const originalTitle = document.title;
        document.title = "🚨 NUEVA AUDITORÍA ASIGNADA 🚨";

        // Sonido de alerta
        try {
          const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();

          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);

          oscillator.frequency.value = 800;
          oscillator.type = 'sine';
          gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);

          oscillator.start();
          oscillator.stop(audioContext.currentTime + 0.3);
        } catch (audioError) {
          console.log('No se pudo reproducir sonido de alerta');
        }

        // Alerta visual solo cuando hay interrupción
        const shouldSwitch = confirm(
          `¿Tomar auditoría asignada?`
        );

        if (shouldSwitch) {
          // Guardar progreso actual antes de cambiar
          await saveCurrentProgress();

          // Cerrar auditoría actual y abrir la nueva
          onClose(); // Esto cierra el AuditTemplate actual

          // El componente padre debería manejar la apertura de la nueva auditoría
          // Usamos el callback onComplete para comunicar la nueva asignación
          onComplete({
            type: 'NEW_ASSIGNMENT_DETECTED',
            newAuditId: newAssignment.id,
            message: 'Cambio automático a nueva auditoría asignada'
          });
        } else {
          // Si no quiere cambiar, reanudar el timer
          startTimer();
          setIsPaused(false);
        }

        // Restaurar título después de 30 segundos
        setTimeout(() => {
          document.title = originalTitle;
        }, 30000);
      }
      // Si no hay trabajo activo (ciclo normal), no hacer nada - que inicie normalmente

    } catch (error) {
      console.error('Error manejando nueva asignación:', error);
    }
  };

  // Guardar progreso actual de forma segura
  const saveCurrentProgress = async () => {
    try {
      if (events.length > 0 || observacionesFinales || sucursalDetails || canalesVision) {
        // Aquí podrías implementar un guardado automático
        // Por ahora solo logueamos que se debería guardar
        console.log('💾 Guardando progreso automáticamente antes de cambiar auditoría...');

        // Si hay eventos o contenido, podrías llamar a la función de guardar
        // await saveAuditProgress();
      }
    } catch (error) {
      console.error('Error guardando progreso:', error);
    }
  };

  const startTimer = () => {
    intervalRef.current = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);
  };

  const pauseTimer = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimeStatus = () => {
    if (!template) return { status: 'normal', percentage: 0 };
    
    const horasEstimadas = template.auditoria.horasEstimadas || 2.0;
    const segundosEstimados = horasEstimadas * 3600;
    const percentage = (elapsedTime / segundosEstimados) * 100;
    
    if (percentage >= 150) return { status: 'exceeded', percentage };
    if (percentage >= 100) return { status: 'overtime', percentage };
    if (percentage >= 75) return { status: 'warning', percentage };
    return { status: 'normal', percentage };
  };

  const getTimerColor = () => {
    const { status } = getTimeStatus();
    switch (status) {
      case 'exceeded': return '#dc3545'; // Rojo
      case 'overtime': return '#fd7e14'; // Naranja
      case 'warning': return '#ffc107'; // Amarillo
      default: return '#28a745'; // Verde
    }
  };

  const getTimerMessage = () => {
    const { status, percentage } = getTimeStatus();
    if (!template) return '';
    
    const horasEstimadas = template.auditoria.horasEstimadas;
    switch (status) {
      case 'exceeded': 
        return `⚠️ Tiempo excedido (${percentage.toFixed(0)}% de ${horasEstimadas}h estimadas)`;
      case 'overtime': 
        return `🕐 Tiempo extra (${percentage.toFixed(0)}% de ${horasEstimadas}h estimadas)`;
      case 'warning': 
        return `⏰ Próximo al límite (${percentage.toFixed(0)}% de ${horasEstimadas}h estimadas)`;
      default: 
        return `✅ En tiempo (${percentage.toFixed(0)}% de ${horasEstimadas}h estimadas)`;
    }
  };

  const addEvent = () => {
    const newEvent: AuditEvent = {
      id: Date.now(),
      numero: events.length + 1,
      timestamp: '', // Campo vacío para que el operador ingrese manualmente
      descripcion: '',
      observaciones: '',
      imagenes: [],
      tipoEvento: 'infraccion-menor', // Por defecto azul
      colorEvento: '#0066cc'
    };
    setEvents([...events, newEvent]);
  };

  const updateEvent = (eventId: number, field: keyof AuditEvent, value: any) => {
    setEvents(events.map(event => 
      event.id === eventId ? { ...event, [field]: value } : event
    ));
  };

  const deleteEvent = (eventId: number) => {
    const updatedEvents = events.filter(event => event.id !== eventId);
    const renumberedEvents = updatedEvents.map((event, index) => ({
      ...event,
      numero: index + 1
    }));
    setEvents(renumberedEvents);
  };

  const handlePaste = async (e: React.ClipboardEvent, eventId: number) => {
    const items = e.clipboardData.items;
    
    for (let item of items) {
      if (item.type.indexOf('image') !== -1) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          await processImage(file, eventId);
        }
      }
    }
  };

  const processImage = async (file: File, eventId: number): Promise<void> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Tamaño fijo estándar para todas las imágenes
        const standardWidth = 600;
        const standardHeight = 400;
        
        canvas.width = standardWidth;
        canvas.height = standardHeight;
        
        if (ctx) {
          // Calcular el recorte/ajuste para mantener aspecto y llenar el canvas
          const sourceAspect = img.width / img.height;
          const targetAspect = standardWidth / standardHeight;
          
          let drawWidth = img.width;
          let drawHeight = img.height;
          let sx = 0;
          let sy = 0;
          
          if (sourceAspect > targetAspect) {
            // Imagen más ancha - recortar los lados
            drawWidth = img.height * targetAspect;
            sx = (img.width - drawWidth) / 2;
          } else {
            // Imagen más alta - recortar arriba y abajo
            drawHeight = img.width / targetAspect;
            sy = (img.height - drawHeight) / 2;
          }
          
          // Dibujar la imagen centrada y ajustada al tamaño estándar
          ctx.drawImage(img, sx, sy, drawWidth, drawHeight, 0, 0, standardWidth, standardHeight);
        }
        
        canvas.toBlob((blob) => {
          if (blob) {
            const reader = new FileReader();
            reader.onload = (e) => {
              const imageData: EventImage = {
                id: Date.now(),
                data: e.target?.result as string,
                name: `evento_${eventId}_${Date.now()}.jpg`,
                size: blob.size,
                timestamp: new Date().toLocaleString('es-ES')
              };
              
              const event = events.find(event => event.id === eventId);
              if (event) {
                updateEvent(eventId, 'imagenes', [...event.imagenes, imageData]);
              }
              
              resolve();
            };
            reader.readAsDataURL(blob);
          }
        }, 'image/jpeg', 0.85);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  const removeImage = (eventId: number, imageId: number) => {
    const event = events.find(e => e.id === eventId);
    if (event) {
      const updatedImages = event.imagenes.filter(img => img.id !== imageId);
      updateEvent(eventId, 'imagenes', updatedImages);
    }
  };

  const downloadAttachment = async (filename: string) => {
    try {
      await auditsApi.downloadFile(auditId, filename);
    } catch (err: any) {
      setError('Error descargando archivo: ' + (err.message || 'Error desconocido'));
    }
  };

  // Función original para operadores (finalizar auditoría)
  const handleFinalizarOperador = async () => {
    setIsLoading(true);
    const timeoutId = setTimeout(() => {
      setError('La operación está tardando más de lo esperado. Verifique su conexión.');
      setIsLoading(false);
    }, 60000);

    try {
      const auditData = {
        eventos: events,
        observacionesFinales,
        tiempoTrabajado: elapsedTime,
        fechaUltimaModificacion: new Date().toISOString(),
        estado: 'terminada',
        sucursalDetails,
        canalesVision
      };

      await Promise.race([
        onComplete(auditData),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Operation timeout')), 45000)
        )
      ]);

      clearTimeout(timeoutId);
      // El modal se cierra automáticamente cuando onComplete termina exitosamente
    } catch (error: any) {
      clearTimeout(timeoutId);
      console.error('Error finalizando auditoría:', error);
      setError('Error al finalizar la auditoría: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsLoading(false);
    }
  };

  // Función para supervisor/admin (guardar cambios sin enviar)
  const handleGuardarSupervisor = async () => {
    setIsLoading(true);
    const timeoutId = setTimeout(() => {
      setError('La operación está tardando más de lo esperado. Verifique su conexión.');
      setIsLoading(false);
    }, 60000);

    try {
      const auditData = {
        eventos: events,
        observacionesFinales,
        tiempoTrabajado: elapsedTime,
        fechaUltimaModificacion: new Date().toISOString(),
        estado: 'guardado_supervisor', // Estado especial para evitar conflictos
        sucursalDetails,
        canalesVision
      };
      
      await Promise.race([
        onComplete(auditData),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Operation timeout')), 45000)
        )
      ]);

      clearTimeout(timeoutId);

      // Mostrar mensaje de éxito
      alert('✅ Cambios guardados exitosamente');

      // Cerrar el modal después de guardar exitosamente
      if (window.opener) {
        // Si se abrió en nueva ventana, cerrarla
        window.close();
      } else {
        // Si no, usar la función onClose
        onClose();
      }
    } catch (error: any) {
      clearTimeout(timeoutId);
      console.error('Error guardando cambios:', error);
      setError('Error al guardar cambios: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsLoading(false);
    }
  };

  // Función para aprobar y enviar al cliente (solo supervisor/admin)
  const handleApproveAndSend = async () => {
    if (!window.confirm('⚠️ ¿Estás seguro de APROBAR y ENVIAR esta auditoría al cliente? Esta acción no se puede deshacer.')) {
      return;
    }

    setIsLoading(true);
    const timeoutId = setTimeout(() => {
      setError('La operación está tardando más de lo esperado. Verifique su conexión.');
      setIsLoading(false);
    }, 60000);

    try {
      const auditData = {
        eventos: events,
        observacionesFinales,
        tiempoTrabajado: elapsedTime,
        fechaUltimaModificacion: new Date().toISOString(),
        estado: 'aprobada', // Estado especial para aprobación
        sucursalDetails,
        canalesVision
      };
      
      await Promise.race([
        onComplete(auditData),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Operation timeout')), 45000)
        )
      ]);
      
      clearTimeout(timeoutId);
      alert('✅ Auditoría aprobada y enviada al cliente exitosamente.');
      // El modal se cierra automáticamente cuando onComplete termina exitosamente
    } catch (error: any) {
      clearTimeout(timeoutId);
      console.error('Error aprobando y enviando auditoría:', error);
      setError('Error al aprobar y enviar la auditoría: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsLoading(false);
    }
  };

  const handlePauseAudit = async (reason: string) => {
    setIsPaused(true);
    pauseTimer();
    
    try {
      const pauseData = {
        eventos: events,
        observacionesFinales,
        tiempoTrabajado: elapsedTime,
        fechaPausa: new Date().toISOString(),
        razonPausa: reason,
        estado: 'pausada',
        sucursalDetails,
        canalesVision
      };
      
      // Cerrar modal de pausa y completar
      setShowPauseDialog(false);
      setPauseReason('');
      await onComplete(pauseData);
    } catch (error: any) {
      console.error('Error pausando auditoría:', error);
      setError('Error al pausar la auditoría: ' + error.message);
      setShowPauseDialog(false);
      setPauseReason('');
    }
  };

  const handleComplete = () => {
    if (events.length === 0) {
      const confirm = window.confirm(
        'No hay eventos registrados. ¿Estás seguro de completar la auditoría?'
      );
      if (!confirm) return;
    }

    // console.log('📋 DATOS FINALES:', {
    //   eventos: events,
    //   eventosCount: events.length,
    //   primerosEventos: events.slice(0, 2),
    //   observacionesFinales,
    //   tiempoTrabajado: elapsedTime,
    //   sucursalDetails,
    //   canalesVision
    // });

    const resultData = {
      eventos: events,
      observacionesFinales,
      tiempoTrabajado: elapsedTime,
      sucursalDetails,
      canalesVision
    };

    onComplete(resultData);
  };

  if (loading) {
    return (
      <div className="audit-template-overlay">
        <div style={{ 
          background: 'white', 
          padding: '2rem', 
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div>⏳ Cargando plantilla de auditoría...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="audit-template-overlay">
        <div style={{ 
          background: 'white', 
          padding: '2rem', 
          borderRadius: '8px',
          textAlign: 'center',
          maxWidth: '400px'
        }}>
          <div style={{ color: '#dc3545', marginBottom: '1rem' }}>❌ {error}</div>
          <button onClick={onClose} style={{
            background: '#6c757d',
            color: 'white',
            border: 'none',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            cursor: 'pointer'
          }}>
            Cerrar
          </button>
        </div>
      </div>
    );
  }

  if (!template) return null;

  const clientInfo = {
    nombre: template.auditoria.cliente,
    empresa: template.auditoria.cliente,
    direccion: template.auditoria.direccion,
    sucursal: template.auditoria.sucursal
  };

  return (
    <div className="audit-template-overlay">
      <div className="audit-template">
        {/* Header con logo empresa */}
        <div className="template-header">
          <div className="company-logo">
            <img 
              src="D:/prints/LogoMembrete.png" 
              alt="Lo Veo Por Vos - Servicio de Auditoría mediante Monitoreo por Cámaras" 
              className="company-logo-img"
            />
          </div>
          
          <div className="audit-controls">
            {user?.role !== 'operador' && template && (
              <>
                <div className="timer-display" style={{ background: `${getTimerColor()}20` }}>
                  <span className="timer-label">⏱️ Tiempo:</span>
                  <span className="timer-value" style={{ color: getTimerColor() }}>
                    {formatTime(elapsedTime)}
                  </span>
                </div>
                <div style={{ 
                  fontSize: '0.8rem', 
                  color: getTimerColor(), 
                  fontWeight: '500',
                  maxWidth: '200px',
                  textAlign: 'center',
                  lineHeight: '1.2'
                }}>
                  {getTimerMessage()}
                </div>
              </>
            )}
            
            <button 
              onClick={() => setShowPauseDialog(true)}
              className="btn-pause"
              disabled={isPaused}
            >
              ⏸️ Pausar Auditoría
            </button>
            
            {/* Botones diferentes según el rol */}
            {supervisorMode ? (
              <>
                {/* Botón Guardar Cambios para supervisor/admin */}
                <button 
                  onClick={handleGuardarSupervisor}
                  className="btn-save"
                  disabled={isLoading}
                  style={{
                    backgroundColor: '#f59e0b',
                    color: 'white',
                    marginRight: '10px'
                  }}
                >
                  {isLoading ? '⏳ Guardando...' : '💾 Guardar Cambios'}
                </button>
                
                {/* Botón Aprobar y Enviar para supervisor/admin */}
                <button 
                  onClick={handleApproveAndSend}
                  className="btn-approve"
                  disabled={isLoading}
                  style={{
                    backgroundColor: '#dc2626',
                    color: 'white'
                  }}
                >
                  {isLoading ? '⏳ Enviando...' : '🚀 Aprobar y Enviar al Cliente'}
                </button>
              </>
            ) : (
              /* Botón normal para operadores */
              <>
                {autosaveStatus !== 'idle' && (
                  <span style={{
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    marginRight: '10px',
                    color: autosaveStatus === 'error' ? '#dc2626'
                         : autosaveStatus === 'saving' ? '#6b7280' : '#16a34a'
                  }}>
                    {autosaveStatus === 'saving' ? '💾 Guardando…'
                     : autosaveStatus === 'saved' ? '✅ Guardado automático'
                     : '⚠️ No se pudo autoguardar'}
                  </span>
                )}
                <button
                  onClick={handleFinalizarOperador}
                  className="btn-save"
                  disabled={isLoading}
                >
                  {isLoading ? '⏳ Finalizando...' : '✅ Finalizar Auditoría'}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Modo supervisor indicator */}
        {supervisorMode && (
          <div style={{
            backgroundColor: '#1e40af',
            color: 'white',
            padding: '12px 20px',
            margin: '0 -20px 20px -20px',
            textAlign: 'center',
            fontSize: '0.95rem',
            fontWeight: '600',
            borderLeft: '4px solid #3b82f6'
          }}>
            🔧 MODO SUPERVISIÓN - Editando la planilla del operador {initialData?.meta?.operadorNombre}
          </div>
        )}

        {/* Título de la auditoría */}
        <div className="audit-title-section">
          <h2>🎯 {template.auditoria.titulo || 'Auditoría Sin Título'}</h2>
          {template.auditoria.descripcion && (
            <p className="audit-description" style={{ whiteSpace: 'pre-wrap' }}>{template.auditoria.descripcion}</p>
          )}
        </div>

        {/* Archivos Adjuntos */}
        {template.archivosAdjuntos && template.archivosAdjuntos.length > 0 && (
          <div className="attached-files-section" style={{
            background: '#e3f2fd',
            border: '2px solid #2196f3',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px'
          }}>
            <h3 style={{ 
              margin: '0 0 16px 0', 
              color: '#1976d2',
              fontSize: '1.25rem',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              📎 Archivos del Cliente ({template.archivosAdjuntos.length})
            </h3>
            <p style={{ 
              margin: '0 0 12px 0', 
              fontSize: '0.9rem',
              color: '#424242'
            }}>
              El cliente adjuntó estos archivos con la solicitud de auditoría:
            </p>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
              gap: '12px'
            }}>
              {template.archivosAdjuntos.map((archivo: any, index: number) => (
                <button 
                  key={index} 
                  onClick={() => downloadAttachment(archivo.nombreOriginal || archivo.name || `archivo_${index + 1}`)}
                  style={{
                    background: 'white',
                    border: '1px solid #bbdefb',
                    borderRadius: '6px',
                    padding: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    width: '100%',
                    textAlign: 'left'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = '#f0f8ff';
                    e.currentTarget.style.borderColor = '#2196f3';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(33, 150, 243, 0.2)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'white';
                    e.currentTarget.style.borderColor = '#bbdefb';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{
                    background: '#2196f3',
                    color: 'white',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}>
                    📄
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontWeight: '600',
                      fontSize: '0.875rem',
                      color: '#1976d2',
                      wordBreak: 'break-word',
                      marginBottom: '2px'
                    }}>
                      {archivo.nombreOriginal || archivo.name || `Archivo ${index + 1}`}
                    </div>
                    <div style={{
                      fontSize: '0.75rem',
                      color: '#666',
                      display: 'flex',
                      gap: '8px'
                    }}>
                      {archivo.size && (
                        <span>📊 {Math.round(archivo.size / 1024)} KB</span>
                      )}
                      {archivo.type && (
                        <span>🎯 {archivo.type}</span>
                      )}
                      <span style={{ marginLeft: 'auto', color: '#2196f3', fontWeight: '500' }}>
                        ⬇️ Clic para descargar
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <div style={{
              marginTop: '12px',
              padding: '8px',
              background: '#fff3e0',
              border: '1px solid #ffb74d',
              borderRadius: '4px',
              fontSize: '0.8rem',
              color: '#e65100'
            }}>
              💡 <strong>Tip:</strong> Estos archivos contienen información importante del cliente. 
              Revísalos antes de comenzar la auditoría para tener el contexto completo.
            </div>
          </div>
        )}

        {/* Información adicional del cliente/sucursal */}
        <div className="client-additional-info-section">
          <h2>📝 Información Adicional</h2>
          <textarea
            value={sucursalDetails}
            onChange={(e) => setSucursalDetails(e.target.value)}
            placeholder="Especificar sucursal, ubicación, horarios, información del DVR, diferencias encontradas, configuraciones especiales, etc."
            rows={4}
            className="client-additional-textarea"
            spellCheck={true}
            lang="es"
          />
        </div>


        {/* Sección de eventos */}
        <div className="events-section">
          <div className="events-header">
            <h2>📝 EVENTOS</h2>
            <button onClick={addEvent} className="btn-add-event">
              {events.length === 0 ? '1 evento' : `➕ Agregar Evento ${events.length + 1}`}
            </button>
          </div>

          {events.length === 0 ? (
            <div className="no-events">
              <p>Haz clic en "1 evento" para comenzar con el primer evento.</p>
            </div>
          ) : (
            <div className="events-list">
              {events.map((event) => (
                <div key={event.id} className="event-item" data-event-id={event.id}>
                  <div className="event-header">
                    <h3>EVENTO #{event.numero}</h3>
                    <div className="event-timestamp-container">
                      <label>📅 Hora:</label>
                      <input
                        type="text" 
                        value={event.timestamp}
                        onChange={(e) => updateEvent(event.id, 'timestamp', e.target.value)}
                        className="event-timestamp-input"
                        placeholder="Ej: 14:30 - 26/08/2025"
                      />
                    </div>
                    <button 
                      onClick={() => deleteEvent(event.id)}
                      className="btn-delete-event"
                    >
                      🗑️
                    </button>
                  </div>
                  
                  <div className="event-content">
                    <textarea
                      value={event.descripcion}
                      onChange={(e) => updateEvent(event.id, 'descripcion', e.target.value)}
                      placeholder="Describe lo que observaste en este evento..."
                      rows={3}
                      className="event-description"
                      spellCheck={true}
                      lang="es"
                    />
                    
                    {/* Selector de tipo y color de evento */}
                    <div className="event-type-selector">
                      <label>🎨 Tipo de Evento:</label>
                      <select 
                        value={event.tipoEvento}
                        onChange={(e) => {
                          const tipoEvento = e.target.value;
                          let colorEvento = '#0066cc'; // azul por defecto
                          
                          switch(tipoEvento) {
                            case 'infraccion-grave':
                              colorEvento = '#dc2626'; // rojo
                              break;
                            case 'infraccion-menor':
                              colorEvento = '#0066cc'; // azul
                              break;
                            case 'problema-tecnico':
                              colorEvento = '#16a34a'; // verde
                              break;
                          }
                          
                          updateEvent(event.id, 'tipoEvento', tipoEvento);
                          updateEvent(event.id, 'colorEvento', colorEvento);
                        }}
                        className="event-type-select"
                      >
                        <option value="infraccion-menor">🔵 Infracción Menor (conducta, disciplina)</option>
                        <option value="infraccion-grave">🔴 Infracción Grave (dinero, productos, normas)</option>
                        <option value="problema-tecnico">🟢 Problema Técnico (cámaras, internet, equipos)</option>
                      </select>
                      <div 
                        className="color-preview" 
                        style={{ 
                          backgroundColor: event.colorEvento,
                          width: '20px', 
                          height: '20px', 
                          borderRadius: '4px',
                          display: 'inline-block',
                          marginLeft: '10px',
                          border: '2px solid #ccc'
                        }}
                      ></div>
                    </div>
                    
                    <div 
                      className="image-paste-area"
                      onPaste={(e) => handlePaste(e, event.id)}
                      onClick={() => {
                        const ref = pasteAreaRefs.current.get(event.id);
                        if (ref) {
                          ref.focus();
                        }
                      }}
                      ref={(el) => {
                        if (el) {
                          pasteAreaRefs.current.set(event.id, el);
                        } else {
                          pasteAreaRefs.current.delete(event.id);
                        }
                      }}
                      tabIndex={0}
                      style={{ cursor: 'pointer' }}
                    >
                      <p>📷 Haz clic aquí y pega capturas de pantalla (Ctrl+V)</p>
                      
                      {event.imagenes && event.imagenes.length > 0 && (
                        <div className="event-images">
                          {event.imagenes.map((img) => (
                            <div key={`image-${event.id}-${img.id}`} className="event-image">
                              <img src={img.data} alt={`Evento ${event.numero}`} />
                              <div className="image-info">
                                <span>{img.timestamp}</span>
                                <button 
                                  onClick={() => removeImage(event.id, img.id)}
                                  className="btn-remove-image"
                                >
                                  ✕
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Canales de Visión Utilizados */}
        <div className="vision-channels-section">
          <h2>📹 Canales de Visión Utilizados</h2>
          <textarea
            value={canalesVision}
            onChange={(e) => setCanalesVision(e.target.value)}
            placeholder="Especificar qué canales fueron revisados, cuáles funcionan correctamente, cuáles tienen problemas, calidad de imagen, cobertura, etc."
            rows={4}
            className="vision-channels-textarea"
            spellCheck={true}
            lang="es"
          />
        </div>

        {/* Observaciones finales */}
        <div className="final-observations-section">
          <h2>📝 Observaciones Finales</h2>
          <textarea
            value={observacionesFinales}
            onChange={(e) => setObservacionesFinales(e.target.value)}
            placeholder="Escribe aquí las conclusiones y observaciones finales de la auditoría..."
            rows={5}
            className="final-observations-textarea"
            spellCheck={true}
            lang="es"
          />
        </div>

        {/* Firma del operador */}
        <div className="signature-section">
          <div className="signature-info">
            <p><strong>Auditor:</strong> {user?.name || 'Sin especificar'}</p>
            <p><strong>Fecha:</strong> {new Date().toLocaleDateString('es-ES')}</p>
            {user?.role !== 'operador' && (
              <p><strong>Tiempo total:</strong> {formatTime(elapsedTime)}</p>
            )}
          </div>
        </div>
      </div>

      {/* Modal de pausa */}
      {showPauseDialog && (
        <div className="audit-template-overlay">
          <div className="pause-modal">
            <div className="modal-header">
              <h2>⏸️ Pausar Auditoría</h2>
            </div>
            
            <div className="modal-content">
              <p>¿Por qué necesitas pausar esta auditoría?</p>
              <textarea
                value={pauseReason}
                onChange={(e) => setPauseReason(e.target.value)}
                placeholder="Ejemplo: Auditoría urgente, falta de información del cliente, problemas técnicos..."
                rows={3}
                required
                spellCheck={true}
                lang="es"
              />
              
              <div className="modal-actions">
                <button 
                  onClick={() => {
                    if (pauseReason.trim()) {
                      handlePauseAudit(pauseReason);
                      setShowPauseDialog(false);
                    } else {
                      alert('Por favor, especifica la razón para pausar');
                    }
                  }}
                  className="btn-confirm-pause"
                >
                  ⏸️ Confirmar Pausa
                </button>
                <button 
                  onClick={() => {
                    setShowPauseDialog(false);
                    setPauseReason('');
                  }}
                  className="btn-cancel"
                >
                  ❌ Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditTemplate;